import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import PendingIcon from "@mui/icons-material/HourglassEmpty";
import SendingIcon from "@mui/icons-material/CloudUpload";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useIntl } from "react-intl";
import {
  selectAllItems,
  selectIsLoading,
} from "@/modules/ticket/ticketQueue/ticketQueueSlice";
import {
  processQueueThunk,
  retryQueueItemThunk,
  deleteQueueItemThunk,
} from "@/modules/ticket/ticketQueue/ticketQueueOperations";
import type { QueueItem } from "@/modules/ticket/ticketQueue/ticketQueueSlice";

interface QueueManagementModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal showing all queue items with management actions
 *
 * Features:
 * - List all queue items with status indicators
 * - Show error messages for failed items
 * - Retry individual items or all failed items
 * - Delete individual items
 * - Manual sync trigger
 */
const QueueManagementModal: React.FC<QueueManagementModalProps> = ({
  open,
  onClose,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const items = useSelector(selectAllItems);
  const isLoading = useSelector(selectIsLoading);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleRetryAll = () => {
    dispatch(processQueueThunk());
  };

  const handleRetryItem = async (itemId: string) => {
    await dispatch(retryQueueItemThunk(itemId));
    dispatch(processQueueThunk());
  };

  const handleDeleteItem = async (itemId: string) => {
    setDeletingId(itemId);
    await dispatch(deleteQueueItemThunk(itemId));
    setDeletingId(null);
  };

  const getStatusIcon = (status: QueueItem["status"]) => {
    switch (status) {
      case "pending":
        return <PendingIcon color="primary" />;
      case "sending":
        return <SendingIcon color="action" />;
      case "sent":
        return <CheckCircleIcon color="success" />;
      case "error":
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  const getStatusChip = (item: QueueItem) => {
    const statusLabels = {
      pending: intl.formatMessage({
        id: "queue.status.pendingLabel",
        defaultMessage: "Pending",
      }),
      sending: intl.formatMessage({
        id: "queue.status.sending",
        defaultMessage: "Sending...",
      }),
      sent: intl.formatMessage({
        id: "queue.status.sent",
        defaultMessage: "Sent",
      }),
      error: intl.formatMessage({
        id: "queue.status.errorLabel",
        defaultMessage: "Failed",
      }),
    };

    const colors = {
      pending: "primary" as const,
      sending: "default" as const,
      sent: "success" as const,
      error: "error" as const,
    };

    return (
      <Chip
        label={statusLabels[item.status]}
        color={colors[item.status]}
        size="small"
        sx={{ ml: 1 }}
      />
    );
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString(intl.locale, {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  const errorItems = items.filter((item) => item.status === "error");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {intl.formatMessage({
          id: "queue.modal.title",
          defaultMessage: "Sync Queue",
        })}
      </DialogTitle>

      <DialogContent dividers>
        {items.length === 0 ? (
          <Box textAlign="center" py={3}>
            <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="body1" color="textSecondary">
              {intl.formatMessage({
                id: "queue.modal.empty",
                defaultMessage: "Queue is empty - all items synced",
              })}
            </Typography>
          </Box>
        ) : (
          <>
            {errorItems.length > 0 && (
              <Box mb={2}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RefreshIcon />}
                  onClick={handleRetryAll}
                  disabled={isLoading}
                  fullWidth
                >
                  {intl.formatMessage(
                    {
                      id: "queue.modal.retryAll",
                      defaultMessage: "Retry all failed items ({count})",
                    },
                    { count: errorItems.length },
                  )}
                </Button>
              </Box>
            )}

            <List>
              {items.map((item, index) => (
                <React.Fragment key={item.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    secondaryAction={
                      <Box>
                        {item.status === "error" && (
                          <IconButton
                            edge="end"
                            aria-label="retry"
                            onClick={() => handleRetryItem(item.id)}
                            disabled={isLoading}
                            color="primary"
                            sx={{ mr: 1 }}
                          >
                            <RefreshIcon />
                          </IconButton>
                        )}
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeleteItem(item.id)}
                          disabled={
                            isLoading ||
                            deletingId === item.id ||
                            item.status === "sending"
                          }
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemIcon>{getStatusIcon(item.status)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1" component="span">
                            {item.data.subject || "Untitled"}
                          </Typography>
                          {getStatusChip(item)}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography
                            variant="caption"
                            display="block"
                            color="textSecondary"
                          >
                            {formatTimestamp(item.timestamp)}
                          </Typography>
                          {item.error && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="error"
                              sx={{ mt: 0.5 }}
                            >
                              {intl.formatMessage({
                                id: "queue.modal.error",
                                defaultMessage: "Error:",
                              })}{" "}
                              {item.error}
                            </Typography>
                          )}
                          {item.retryCount > 0 && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="textSecondary"
                            >
                              {intl.formatMessage(
                                {
                                  id: "queue.modal.retryCount",
                                  defaultMessage: "Retry attempts: {count}",
                                },
                                { count: item.retryCount },
                              )}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {intl.formatMessage({
            id: "queue.modal.close",
            defaultMessage: "Close",
          })}
        </Button>
        {items.length > 0 && (
          <Button
            onClick={handleRetryAll}
            disabled={isLoading}
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
          >
            {intl.formatMessage({
              id: "queue.modal.syncNow",
              defaultMessage: "Sync Now",
            })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QueueManagementModal;
